import React, { useEffect, useState } from "react";
import { Settings, Save, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";

/**
 * Owner-only platform settings.
 * - TREL fee / Concierge payout splits (must sum to 100).
 * - Host referral percent (paid from TREL share).
 * - Verification fee, TREL+ price, Elite screening fee.
 * - Demo mode toggle (gates the founder one-click login panel).
 */
export default function OwnerSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/owner/settings");
      setSettings(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load settings.");
    }
  };
  // `load` is intentionally stable across renders (no external deps). The
  // initial fetch only needs to fire once on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const setField = (key, value) => {
    setSettings(s => ({ ...s, [key]: value }));
    setMsg(""); setErr("");
  };

  const handleNumber = (key) => (e) => {
    const v = e.target.value === "" ? "" : Number(e.target.value);
    setField(key, v);
  };

  const splitsValid = settings &&
    Math.abs((Number(settings.trel_fee_percent) + Number(settings.provider_payout_percent)) - 100) < 0.01;

  const save = async () => {
    setErr(""); setMsg("");
    if (!splitsValid) { setErr("TREL fee + Concierge payout must total 100%."); return; }
    setSaving(true);
    try {
      const payload = {
        trel_fee_percent: Number(settings.trel_fee_percent),
        provider_payout_percent: Number(settings.provider_payout_percent),
        host_referral_percent: Number(settings.host_referral_percent),
        verification_fee: Number(settings.verification_fee),
        trel_plus_price: Number(settings.trel_plus_price),
        elite_screening_fee: Number(settings.elite_screening_fee),
        demo_mode: Boolean(settings.demo_mode),
      };
      const { data } = await api.patch("/owner/settings", payload);
      setSettings(data);
      setMsg("Settings saved.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed.");
    }
    setSaving(false);
  };

  if (!settings) {
    return <div className="p-8 text-center text-[#071A2D]/60">Loading settings…</div>;
  }

  return (
    <div data-testid="owner-settings-panel" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#064E3B] font-semibold flex items-center gap-1.5">
            <Settings size={12}/> Platform economics
          </div>
          <div className="text-sm text-[#071A2D]/70 mt-0.5">
            Adjust splits and pricing for the entire marketplace. Owner-only.
          </div>
        </div>
        <button
          data-testid="owner-settings-save"
          onClick={save}
          disabled={saving}
          className="btn-emerald text-sm px-5 py-2 flex items-center gap-1.5"
        >
          <Save size={14}/> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      {err && (
        <div data-testid="owner-settings-error" className="p-3 rounded-xl bg-[#FF7A45]/10 border border-[#FF7A45]/30 text-[#FF7A45] text-sm flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 shrink-0"/> {err}
        </div>
      )}
      {msg && (
        <div data-testid="owner-settings-msg" className="p-3 rounded-xl bg-[#064E3B]/10 border border-[#064E3B]/30 text-[#064E3B] text-sm">
          {msg}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <NumberField
          label="TREL fee %"
          testid="settings-trel-fee"
          value={settings.trel_fee_percent}
          onChange={handleNumber("trel_fee_percent")}
          hint="Platform commission per booking subtotal."
        />
        <NumberField
          label="Concierge payout %"
          testid="settings-provider-payout"
          value={settings.provider_payout_percent}
          onChange={handleNumber("provider_payout_percent")}
          hint="Paid to the Concierge per booking. TREL + Concierge must total 100%."
        />
        <NumberField
          label="Host referral %"
          testid="settings-host-referral"
          value={settings.host_referral_percent}
          onChange={handleNumber("host_referral_percent")}
          hint="Paid to the referring Host. Comes out of TREL's share — Concierge payout never changes."
        />
        <NumberField
          label="Verification fee ($)"
          testid="settings-verification-fee"
          value={settings.verification_fee}
          onChange={handleNumber("verification_fee")}
          hint="One-time Concierge identity verification fee."
        />
        <NumberField
          label="TREL+ price ($/mo)"
          testid="settings-trel-plus"
          value={settings.trel_plus_price}
          onChange={handleNumber("trel_plus_price")}
          hint="Monthly TREL+ subscription price for Concierges."
        />
        <NumberField
          label="Elite screening fee ($)"
          testid="settings-elite-fee"
          value={settings.elite_screening_fee}
          onChange={handleNumber("elite_screening_fee")}
          hint="Background screening fee paid by Elite Concierges."
        />
      </div>

      {/* Live split validation card */}
      <div
        className={`p-4 rounded-2xl border ${
          splitsValid
            ? "bg-[#064E3B]/8 border-[#064E3B]/25 text-[#064E3B]"
            : "bg-[#FF7A45]/10 border-[#FF7A45]/30 text-[#FF7A45]"
        }`}
      >
        <div className="text-[10px] uppercase tracking-widest font-semibold">Payment split preview</div>
        <div className="text-sm mt-1">
          On every $100 subtotal: <strong>${Number(settings.trel_fee_percent || 0).toFixed(0)}</strong> TREL ·{" "}
          <strong>${Number(settings.provider_payout_percent || 0).toFixed(0)}</strong> Concierge ·{" "}
          <strong>${Number(settings.host_referral_percent || 0).toFixed(0)}</strong> Host (from TREL share).
        </div>
        {!splitsValid && (
          <div className="text-xs mt-1">TREL + Concierge must total exactly 100%.</div>
        )}
      </div>

      {/* Demo mode */}
      <div className="p-4 rounded-2xl border border-[#071A2D]/10 bg-[#FFF8EE]">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#064E3B] font-semibold">Demo access</div>
            <div className="text-sm text-[#071A2D]/70 mt-0.5">
              When enabled, the login page shows founder one-click buttons (Admin / Customer / Provider / Host). Disable for production.
            </div>
          </div>
          <button
            data-testid="settings-demo-toggle"
            onClick={() => setField("demo_mode", !settings.demo_mode)}
            className={`w-14 h-7 rounded-full transition relative ${settings.demo_mode ? "bg-[#064E3B]" : "bg-[#071A2D]/15"}`}
          >
            <span
              className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all ${
                settings.demo_mode ? "left-8" : "left-1"
              }`}
            />
          </button>
        </div>
        <div className="text-[11px] text-[#071A2D]/55 mt-2">
          Note: This persists the new value to the database. Runtime <code>DEMO_MODE</code> env var still controls server-side gating on next boot — see `/api/config`.
        </div>
      </div>
    </div>
  );
}

const NumberField = ({ label, value, onChange, hint, testid }) => (
  <div className="rounded-2xl border border-[#071A2D]/10 bg-[#FFF8EE] p-4">
    <label className="text-[10px] uppercase tracking-widest text-[#064E3B] font-semibold">{label}</label>
    <input
      data-testid={testid}
      type="number"
      step="0.01"
      value={value ?? ""}
      onChange={onChange}
      className="kbd-input mt-2"
    />
    {hint && <div className="text-[11px] text-[#071A2D]/55 mt-1.5">{hint}</div>}
  </div>
);
